/**
 * Azure OpenAI GPT-4o Service
 * Handles AI-powered clinical note formatting and enhancement
 */

class AIService {
  constructor() {
    this.endpoint = process.env.REACT_APP_OPENAI_ENDPOINT;
    this.apiKey = process.env.REACT_APP_OPENAI_KEY;
    this.deployment = process.env.REACT_APP_OPENAI_DEPLOYMENT;
    this.apiVersion = '2024-02-01';
    
    this.baseUrl = `${this.endpoint}/openai/deployments/${this.deployment}/chat/completions?api-version=${this.apiVersion}`;
  }

  /**
   * Format raw voice transcript into professional clinical note
   * @param {string} transcript - Raw voice transcript from therapist
   * @param {Object} template - Note template with field structure
   * @param {Object} options - Additional formatting options
   */
  async formatClinicalNote(transcript, template, options = {}) {
    try {
      if (!this.apiKey || !this.endpoint || !this.deployment) {
        throw new Error('Azure OpenAI credentials missing in environment variables');
      }

      console.log('AI Service: Received template:', template);
      console.log('AI Service: Template fields:', template?.fields);
      const prompt = this.buildClinicalNotePrompt(transcript, template, options);
      
      const response = await this.callGPT4o(prompt, {
        temperature: 0.3, // Lower temperature for consistent clinical formatting
        max_tokens: 1500,
        top_p: 0.9
      });

      return {
        success: true,
        formattedNote: response,
        originalTranscript: transcript,
        template: template.name,
        timestamp: new Date().toISOString()
      };

    } catch (error) {
      console.error('Error formatting clinical note:', error);
      return {
        success: false,
        error: error.message,
        originalTranscript: transcript,
        template: template.name
      };
    }
  }

  /**
   * Build the prompt for clinical note formatting
   */
  buildClinicalNotePrompt(transcript, template, options) {
    const templateFields = template.fields || ['Progress', 'Intervention', 'Response'];
    const templateName = template.noteType || template.name || 'Clinical Note';

    const lower = templateName.toLowerCase();
    let contextGuidance;
    if (lower.includes('emdr')) {
      contextGuidance = 'This is an EMDR session note. Use trauma-processing language including references to target memories, desensitization phases, SUD levels, installation, and body scan as applicable.';
    } else if (lower.includes('group')) {
      contextGuidance = 'This is a group therapy note. Reference group dynamics, member participation, therapeutic factors, and group-level interventions appropriately.';
    } else if (lower.includes('couple') || lower.includes('family')) {
      contextGuidance = 'This is a couples or family therapy note. Address relational dynamics, communication patterns, and systemic interventions as appropriate.';
    } else if (lower.includes('crisis')) {
      contextGuidance = 'This is a crisis assessment note. Prioritize risk factors, protective factors, safety planning, and clinical decision-making around level of care.';
    } else if (lower.includes('supervision')) {
      contextGuidance = 'This is a clinical supervision note. Document supervisee performance, case conceptualization quality, skill development, and supervision directives given.';
    } else if (lower.includes('intake') || lower.includes('initial') || lower.includes('assessment') || lower.includes('evaluation')) {
      contextGuidance = 'This is an intake or assessment document. Include presenting concerns, biopsychosocial history, mental status, risk assessment, diagnostic impressions, and treatment recommendations.';
    } else if (lower.includes('terminat') || lower.includes('discharge') || lower.includes('closing')) {
      contextGuidance = 'This is a termination or discharge summary. Summarize treatment goals, progress achieved, clinical status at discharge, and aftercare recommendations.';
    } else if (lower.includes('collateral') || lower.includes('contact')) {
      contextGuidance = 'This is a collateral contact note. Document the purpose of contact, who was contacted, information shared/received, and clinical relevance to treatment.';
    } else if (lower.includes('treatment plan')) {
      contextGuidance = 'This is a treatment plan update. Address progress toward existing goals, any goal modifications, updated interventions, and revised timeline as appropriate.';
    } else {
      contextGuidance = 'This is a ' + templateName + '. Apply clinical documentation standards appropriate to this type of note, using professional terminology and organized, thorough clinical language.';
    }

    const sectionList = templateFields.map(function(f) { return '- ' + f; }).join('\n');
    const outputFormat = templateFields.map(function(f) { return '**' + f + ':**\n[Clinical content here]\n'; }).join('\n');

    const prompt = [
      'You are an expert clinical documentation assistant for behavioral health professionals.',
      'Your job is to convert a therapist\'s voice note into a polished, HIPAA-compliant clinical document.',
      '',
      '**DOCUMENT TYPE:** ' + templateName,
      '**CONTEXT:** ' + contextGuidance,
      '',
      '**CORE PRINCIPLE — BE CONCISE.**',
      'Match the length and depth of the note to what the therapist actually said. Do not pad. Do not restate the same clinical observation across sections in different words. A brief dictation should produce a brief note; a rich dictation can produce a richer note. Prefer one accurate clinical sentence over three vague ones.',
      '',
      '**INSTRUCTIONS:**',
      '1. Convert casual speech into professional clinical language while preserving the therapist\'s clinical voice. Elevate tightly — do not stack adjectives or add filler clauses.',
      '2. Populate each section from what the therapist actually said. Every specific fact (events, quotes, scores, interventions, diagnoses, homework) must come from the voice note.',
      '3. **Safety-net baselines for Mental-Status-Exam fields ONLY.** For the following field types — Appearance, Behavior, Mood, Affect, Speech, Eye Contact, Engagement / Cooperation, Orientation, Thought Process, Thought Content, Insight, Judgment, Risk / Safety — if the therapist did not speak to the field, generate a brief, conservative baseline (one short sentence) inferred from the overall tone and content of the session. Examples:',
      '   • Routine check-in, no distress described → "Affect euthymic and congruent with reported content."',
      '   • Grief or distressing content discussed, affect not named → "Affect dysphoric and congruent with discussion."',
      '   • Risk not raised by therapist and no risk indicators in session → "No acute safety concerns identified during session; risk not formally reassessed."',
      '   • Appearance not described → "Appearance appropriate; grooming and hygiene within normal limits."',
      '   These are safety-net inferences, not findings — the therapist will review and correct as needed.',
      '4. **Do NOT apply baseline inferences to non-MSE content** — interventions used, client statements, homework assignments, treatment-plan progress, diagnoses, scores, plans, or anything event-specific. If the therapist did not speak to those, write "Not addressed in this session." Never invent specific facts.',
      '5. Never fabricate quotes, events, diagnoses, scores, or interventions that the therapist did not mention.',
      '',
      '**TEMPLATE SECTIONS TO COMPLETE:**',
      sectionList,
      '',
      '**THERAPIST VOICE NOTE:**',
      '"' + transcript + '"',
      '',
      '**CONCISE-ELEVATION STYLE GUIDE:**',
      '- "client seemed sad" → "Client presented with depressed mood and constricted affect."',
      '- "we talked about anxiety" → "Explored anxiety symptomatology; identified cognitive distortions."',
      '- "gave homework" → "Assigned between-session practice."',
      '- "client did well" → "Client engaged constructively and demonstrated skill use."',
      '- "no concerns" → "No acute safety concerns identified; client denied SI/HI."',
      'Each elevation should be roughly the same length as the original phrase — not longer.',
      '',
      '**OUTPUT FORMAT — complete each section at the shortest professional length that captures the clinical content:**',
      outputFormat,
      '',
      'Generate the ' + templateName + ' now:'
    ].join('\n');

    return prompt;
  }
  /**
   * Enhance existing clinical note for better professional language
   * @param {string} existingNote - Current clinical note text
   * @param {Object} options - Enhancement options
   */
  async enhanceNote(existingNote, options = {}) {
    try {
      const prompt = `You are a clinical documentation expert. Please enhance this clinical note to be more professional, thorough, and clinically accurate while maintaining all original information.

**ENHANCEMENT GOALS:**
- Improve clinical terminology and language
- Ensure professional tone and structure
- Add clinical insights where appropriate
- Maintain HIPAA compliance
- Keep all factual information intact

**ORIGINAL NOTE:**
${existingNote}

**ENHANCED VERSION:**`;

      const response = await this.callGPT4o(prompt, {
        temperature: 0.3,
        max_tokens: 1200
      });

      return {
        success: true,
        enhancedNote: response,
        originalNote: existingNote
      };

    } catch (error) {
      console.error('Error enhancing clinical note:', error);
      return {
        success: false,
        error: error.message,
        originalNote: existingNote
      };
    }
  }

  /**
   * Generate custom template suggestions based on practice type
   * @param {string} practiceType - Type of practice (e.g., "CBT therapy", "group therapy")
   */
  async suggestTemplateFields(practiceType) {
    try {
      const prompt = `You are a clinical documentation expert. Suggest 3-5 optimal documentation template fields for ${practiceType} practice.

**REQUIREMENTS:**
- Fields should be clinically relevant and comprehensive
- Appropriate for ${practiceType} documentation needs
- Follow standard clinical documentation practices
- Each field should be 1-3 words maximum

**PROVIDE ONLY THE FIELD NAMES, ONE PER LINE:**`;

      const response = await this.callGPT4o(prompt, {
        temperature: 0.5,
        max_tokens: 200
      });

      const suggestedFields = response
        .split('\n')
        .filter(line => line.trim())
        .map(line => line.trim().replace(/^[-•*]\s*/, ''))
        .slice(0, 5);

      return {
        success: true,
        suggestedFields,
        practiceType
      };

    } catch (error) {
      console.error('Error generating template suggestions:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Call Azure OpenAI GPT-4o API
   * @param {string} prompt - The prompt to send
   * @param {Object} options - API options (temperature, max_tokens, etc.)
   */
  async callGPT4o(prompt, options = {}) {
    try {
      const requestBody = {
        messages: [
          {
            role: 'system',
            content: 'You are an expert clinical documentation assistant specializing in mental health progress notes. You help therapists convert their casual voice notes into professional, HIPAA-compliant clinical documentation.'
          },
          {
            role: 'user',
            content: prompt
          }
        ],
        temperature: options.temperature || 0.3,
        max_tokens: options.max_tokens || 1000,
        top_p: options.top_p || 0.9,
        frequency_penalty: 0.0,
        presence_penalty: 0.0
      };

      const response = await fetch(this.baseUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'api-key': this.apiKey
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorData = await response.text();
        throw new Error(`Azure OpenAI API error: ${response.status} - ${errorData}`);
      }

      const data = await response.json();
      
      if (!data.choices || !data.choices[0] || !data.choices[0].message) {
        throw new Error('Invalid response format from Azure OpenAI API');
      }

      return data.choices[0].message.content.trim();

    } catch (error) {
      console.error('Error calling Azure OpenAI API:', error);
      throw new Error(`API call failed: ${error.message}`);
    }
  }

  /**
   * Test Azure OpenAI connection
   */
  async testConnection() {
    try {
      const testPrompt = 'Say "Azure OpenAI connection successful" if you can read this message.';
      
      const response = await this.callGPT4o(testPrompt, {
        max_tokens: 50,
        temperature: 0.1
      });

      return {
        success: true,
        message: 'Azure OpenAI GPT-4o connection successful',
        response: response
      };

    } catch (error) {
      console.error('Azure OpenAI connection test failed:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Validate note content for common issues
   * @param {string} noteContent - Clinical note to validate
   */
  validateNote(noteContent) {
    const issues = [];
    
    // Check for minimum content
    if (noteContent.length < 50) {
      issues.push('Note appears too short for clinical documentation');
    }
    
    // Check for unprofessional language (basic check)
    const unprofessionalWords = ['awesome', 'cool', 'weird', 'crazy', 'nuts'];
    unprofessionalWords.forEach(word => {
      if (noteContent.toLowerCase().includes(word)) {
        issues.push(`Consider replacing informal language: "${word}"`);
      }
    });
    
    // Check for required clinical elements
    const hasClientMention = /\b(client|patient|individual)\b/i.test(noteContent);
    if (!hasClientMention) {
      issues.push('Note should reference client/patient/individual');
    }
    
    return {
      isValid: issues.length === 0,
      issues: issues,
      wordCount: noteContent.split(/\s+/).length
    };
  }

  /**
   * Get default clinical templates
   */
  getDefaultTemplates() {
    return [
      {
        id: 'soap',
        name: 'SOAP Note',
        fields: ['Subjective', 'Objective', 'Assessment', 'Plan'],
        description: 'Standard medical documentation format'
      },
      {
        id: 'dap',
        name: 'DAP Note',
        fields: ['Data', 'Assessment', 'Plan'],
        description: 'Simplified clinical documentation'
      },
      {
        id: 'girp',
        name: 'GIRP Note',
        fields: ['Goals', 'Intervention', 'Response', 'Plan'],
        description: 'Goal-focused therapy documentation'
      },
      {
        id: 'bir',
        name: 'BIR Note',
        fields: ['Behavior', 'Intervention', 'Response'],
        description: 'Behavior-focused documentation'
      },
      {
        id: 'narrative',
        name: 'Narrative Note',
        fields: ['Clinical Narrative'],
        description: 'Free-form clinical narrative'
      }
    ];
  }
}

// Create and export singleton instance
export const aiService = new AIService();
export default aiService;